<template>
  <div class="lora-page">
    <div class="filter-bar">
      <el-select
        v-model="filters.siteId"
        placeholder="All Sites"
        clearable
        @change="loadJobs"
        size="small"
        style="width:200px"
      >
        <el-option
          v-for="s in sites"
          :key="s.id"
          :label="s.name"
          :value="s.id"
        />
      </el-select>
      <el-select
        v-model="filters.status"
        placeholder="All Statuses"
        clearable
        @change="loadJobs"
        size="small"
        style="width:160px"
      >
        <el-option v-for="s in jobStatuses" :key="s" :label="s" :value="s" />
      </el-select>
      <el-button size="small" icon="el-icon-refresh" @click="loadJobs"
        >Refresh</el-button
      >
      <span class="result-count">{{ jobs.length }} jobs</span>
      <el-button
        v-if="$permission('Installation Tool.Jobs.Add')"
        type="primary"
        size="small"
        icon="el-icon-plus"
        @click="openCreateModal"
        >Create Job</el-button
      >
    </div>

    <div class="app-card">
      <el-table
        :data="jobs"
        v-loading="loading"
        class="app-table"
        :max-height="tableHeight"
      >
        <el-table-column prop="name" label="Job Name" min-width="200">
          <template slot-scope="{ row }">
            <el-button type="text" @click="goToJob(row)">{{
              row.name
            }}</el-button>
          </template>
        </el-table-column>
        <el-table-column label="Site" min-width="160" show-overflow-tooltip>
          <template slot-scope="{ row }">{{
            siteNameMap[row.siteId] || row.siteId
          }}</template>
        </el-table-column>
        <el-table-column prop="status" label="Status" width="130">
          <template slot-scope="{ row }">
            <el-tag :type="statusType(row.status)" size="small">{{
              row.status
            }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="Created" width="150">
          <template slot-scope="{ row }">{{
            formatDate(row.createdAt)
          }}</template>
        </el-table-column>
        <el-table-column prop="scheduledDate" label="Scheduled" width="150">
          <template slot-scope="{ row }">{{
            row.scheduledDate ? formatDate(row.scheduledDate) : '--'
          }}</template>
        </el-table-column>
        <el-table-column label="" width="180" align="center">
          <template slot-scope="{ row }">
            <el-button size="small" type="primary" plain @click="goToJob(row)"
              >Open</el-button
            >
            <el-button
              v-if="
                row.status === 'Draft' &&
                  $permission('Installation Tool.Jobs.Delete')
              "
              size="small"
              type="danger"
              plain
              @click.stop="deleteJob(row)"
              >Delete</el-button
            >
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- Create Job Modal -->
    <el-dialog
      title="Create Job"
      :visible.sync="showCreateModal"
      width="480px"
      @close="resetCreateForm"
    >
      <el-form
        :model="createForm"
        :rules="createRules"
        ref="createForm"
        label-width="100px"
      >
        <el-form-item label="Site" prop="siteId">
          <el-select
            v-model="createForm.siteId"
            placeholder="Select a site"
            style="width:100%"
            filterable
          >
            <el-option
              v-for="s in sites"
              :key="s.id"
              :label="s.name"
              :value="s.id"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="Job Name" prop="name">
          <el-input
            v-model="createForm.name"
            placeholder="e.g. Ward 3 Installation Phase 1"
          />
        </el-form-item>
        <el-form-item label="Scheduled">
          <el-date-picker
            v-model="createForm.scheduledDate"
            type="date"
            placeholder="Pick a date (optional)"
            value-format="yyyy-MM-ddTHH:mm:ssZ"
            style="width:100%"
          />
        </el-form-item>
        <el-form-item label="Notes">
          <el-input
            type="textarea"
            :rows="3"
            v-model="createForm.notes"
            placeholder="Optional notes"
          />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button class="save-btn" :loading="creating" @click="createJob"
          >CREATE JOB</el-button
        >
        <el-button class="cancel-btn" @click="showCreateModal = false"
          >CANCEL</el-button
        >
      </span>
    </el-dialog>
  </div>
</template>

<script>
import LoraApiService from '@/services/LoraApiService'
import { loraError } from '@/utils/loraError'
import moment from 'moment'
import loraTableHeight from '@/mixins/loraTableHeight'

export default {
  name: 'LoraJobs',
  mixins: [loraTableHeight],
  computed: {
    siteNameMap() {
      const map = {}
      this.sites.forEach(function(s) {
        map[s.id] = s.name
      })
      return map
    }
  },
  data() {
    return {
      jobs: [],
      sites: [],
      loading: false,
      creating: false,
      filters: { siteId: '', status: '' },
      jobStatuses: ['Draft', 'Published', 'Complete', 'Escalated'],
      showCreateModal: false,
      createForm: { siteId: '', name: '', scheduledDate: null, notes: '' },
      createRules: {
        siteId: [
          { required: true, message: 'Please select a site', trigger: 'change' }
        ],
        name: [{ required: true, message: 'Required', trigger: 'blur' }]
      }
    }
  },
  mounted() {
    this.loadSites()
    this.loadJobs()
  },
  methods: {
    async loadSites() {
      try {
        const res = await LoraApiService.getSites()
        this.sites = res.data || []
      } catch {
        // non-critical
      }
    },
    async loadJobs() {
      this.loading = true
      try {
        let res
        if (this.filters.status === 'Escalated') {
          res = await LoraApiService.getEscalatedJobs()
        } else {
          const params = {}
          if (this.filters.siteId) params.siteId = this.filters.siteId
          if (this.filters.status) params.status = this.filters.status
          res = await LoraApiService.getJobs(params)
        }
        this.jobs = res.data || []
      } catch {
        this.$message.error('Failed to load jobs')
      } finally {
        this.loading = false
      }
    },
    openCreateModal() {
      this.createForm = { siteId: '', name: '', scheduledDate: null, notes: '' }
      this.showCreateModal = true
    },
    resetCreateForm() {
      this.$refs.createForm && this.$refs.createForm.resetFields()
    },
    async createJob() {
      const valid = await this.$refs.createForm.validate().catch(() => false)
      if (!valid) return
      this.creating = true
      try {
        var body = { name: this.createForm.name, notes: this.createForm.notes }
        if (this.createForm.scheduledDate) {
          body.scheduledDate = this.createForm.scheduledDate
        }
        const res = await LoraApiService.createJob(this.createForm.siteId, body)
        this.$message.success('Job created')
        this.showCreateModal = false
        this.$emit('open-job', res.data)
      } catch (e) {
        this.$message.error(loraError(e, 'Failed to create job'))
      } finally {
        this.creating = false
      }
    },
    goToJob(job) {
      this.$emit('open-job', job)
    },
    deleteJob(job) {
      var self = this
      this.$confirm(
        'Delete job "' + job.name + '"? This cannot be undone.',
        'Delete Job',
        {
          type: 'warning',
          confirmButtonText: 'Delete',
          confirmButtonClass: 'el-button--danger',
          beforeClose: function(action, instance, done) {
            if (action !== 'confirm') {
              done()
              return
            }
            instance.confirmButtonLoading = true
            instance.confirmButtonText = 'Deleting...'
            LoraApiService.deleteJob(job.id)
              .then(function() {
                self.$message.success('Job deleted')
                self.loadJobs()
                instance.confirmButtonLoading = false
                instance.confirmButtonText = 'Delete'
                done()
              })
              .catch(function(e) {
                self.$message.error(loraError(e, 'Failed to delete job'))
                instance.confirmButtonLoading = false
                instance.confirmButtonText = 'Delete'
              })
          }
        }
      ).catch(function() {})
    },
    formatDate(d) {
      return d ? moment(d).format('DD MMM YYYY') : '-'
    },
    statusType(s) {
      return (
        {
          Draft: 'info',
          Published: 'primary',
          Submitted: 'warning',
          Complete: 'success'
        }[s] || 'info'
      )
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/theme.scss';

.filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  .result-count {
    margin-left: auto;
    font-size: 13px;
    color: var(--text2);
  }

  /deep/ .el-button--primary {
    background: var(--blue);
    border-color: var(--blue-dk);

    &:hover {
      background: var(--blue-dk);
    }
  }
}
.app-table {
  /deep/ .el-tag {
    border-radius: 99px;
    border: none;
    font-weight: 600;
  }
  /deep/ .el-tag--primary {
    background: var(--blue-bg);
    color: var(--blue);
  }
  /deep/ .el-tag--success {
    background: var(--green-bg);
    color: var(--green);
  }
  /deep/ .el-tag--warning {
    background: var(--amber-bg);
    color: var(--amber);
  }
  /deep/ .el-tag--danger {
    background: var(--red-bg);
    color: var(--red);
  }
  /deep/ .el-tag--info {
    background: var(--bg3);
    color: var(--text2);
  }
}
.count-badge {
  background: var(--blue-bg);
  color: var(--blue);
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
}
.text-danger {
  color: var(--red);
}
</style>
